import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Modal,
  Input as AntInput,
  message,
  Badge,
  Drawer,
  Alert,
  Divider,
  List,
  Progress,
} from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ContainerOutlined,
  HomeOutlined,
  InboxOutlined,
  LinkOutlined,
  ShoppingCartOutlined,
  FileSearchOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import styled from "@emotion/styled";
import {
  Booking,
  PaginationParams,
  Company,
  Port,
  TransportType,
  BookingStatus,
  bookingStatusLabels,
  Container,
  Agent,
  BookingType,
  InvoiceInfo,
  InvoiceLine,
  DeliveryInfo,
} from "../../types/booking";
import {
  isStorageOption,
  isStorageBooking,
  STORAGE_OPTION_LABELS,
} from "../../utils/storageOptionUtils";
import {
  isServiceLevel,
  SERVICE_LEVEL_LABELS,
  SERVICE_LEVEL_VALUES,
  type ServiceLevel,
} from "../../utils/serviceLevelUtils";
import {
  fetchBookings,
  updateBookingStatus,
  fetchAgents,
  linkMultipleBookingsToContainer,
  bulkUpdateBookingStatus,
} from "../../services/bookingService";
import { getStatusColor, formatDate } from "../../utils/bookingUtils";
import {
  bulkAnalyzeStoredCommercialInvoices,
  getPrimaryCommercialInvoicePath,
} from "../../features/commercialInvoiceAnalysis";
import {
  bookingHasDeepCheckCiAnalysis,
  bookingNeedsCiAnalysis,
} from "../../utils/agentBookingCiAnalysis";
import { trackFeatureUsage } from "../../lib/posthog";
import {
  LEANCUSTOMS_DECLARATION_BASE,
  LEANCUSTOMS_ORDER_BASE,
} from "../../constants/leancustoms";
import { supabase } from "../../lib/supabase";
import { SupabaseClient } from "@supabase/supabase-js";
import { fetchContainers } from "../../services/containerService";
import { generateAndDownloadPackingSlip } from "../../services/packingSlipService";
import { fetchMasterContainersForFilter } from "../../services/masterContainerService";
import {
  combineLabelsIntoPDF,
  downloadBlob,
} from "../../utils/labelDownloadUtils";
import BookingTableColumnManager, {
  CustomColumn,
  getSavedColumns,
  applyColumnVisibility,
} from "../BookingTableColumnManager";
import { AgentCiValidityCell } from "../agent/AgentCiValidityCell";
import { downloadAdminBookingsCsv } from "../../utils/adminBookingsExport";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Type cast supabase to avoid type errors
const typedSupabase = supabase as unknown as SupabaseClient;

// Define styled components
const CompactTable = styled.div`
  .compact-table {
    .ant-table {
      font-size: 13px;
    }

    .ant-table-thead > tr > th {
      padding: 8px 16px;
    }

    .ant-table-tbody > tr > td {
      padding: 4px 16px;
    }

    .ant-table-cell {
      white-space: nowrap;
    }

    .ant-tag {
      margin-right: 0;
      font-size: 11px;
      padding: 0 4px;
      line-height: 18px;
    }

    .ant-btn {
      font-size: 12px;
      padding: 0 8px;
      height: 24px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

interface BookingsListProps {
  isPendingList?: boolean;
  onViewDetails: (booking: Booking, defaultTab?: string) => void;
  onRefresh: () => void;
  lastUpdate?: Date | null;
  statusFilter?: string;
  resetFilters?: number | null;
}

const BookingsList: React.FC<BookingsListProps> = ({
  isPendingList = false,
  onViewDetails,
  onRefresh,
  lastUpdate = null,
  statusFilter: externalStatusFilter,
  resetFilters = null,
}) => {
  // Navigation and URL params
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to get URL param values
  const getUrlParam = (key: string): string | null => searchParams.get(key);
  const getUrlNumber = (key: string, defaultValue: number): number => {
    const value = searchParams.get(key);
    return value ? parseInt(value, 10) : defaultValue;
  };
  const getUrlBoolean = (key: string): boolean | null => {
    const value = searchParams.get(key);
    return value === "true" ? true : value === "false" ? false : null;
  };
  const getUrlDateRange = (): [string, string] | null => {
    const start = searchParams.get("dateStart");
    const end = searchParams.get("dateEnd");
    return start && end ? [start, end] : null;
  };

  // State - initialize from URL parameters
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const initialSearch = getUrlParam("search") || "";
  const [searchText, setSearchText] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState<string>(
    getUrlParam("status") || (isPendingList ? BookingStatus.SUBMITTED : "all"),
  );
  const [dateRange, setDateRange] = useState<[string, string] | null>(
    getUrlDateRange(),
  );
  const [pagination, setPagination] = useState({
    current: getUrlNumber("page", 1),
    pageSize: getUrlNumber("pageSize", 20),
    total: 0,
  });
  const [sortField, setSortField] = useState<string | undefined>(
    getUrlParam("sortField") || undefined,
  );
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | undefined>(
    (getUrlParam("sortOrder") as "ascend" | "descend") || undefined,
  );
  const [searchQuery, setSearchQuery] = useState(initialSearch); // Initialize from URL param
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [companyFilter, setCompanyFilter] = useState<string | null>(
    getUrlParam("company"),
  );
  const [transportTypeFilter, setTransportTypeFilter] = useState<string | null>(
    getUrlParam("transportType"),
  );
  const [consolidationFilter, setConsolidationFilter] = useState<
    boolean | null
  >(getUrlBoolean("consolidation"));
  const [containerFilter, setContainerFilter] = useState<string | null>(
    getUrlParam("container"),
  );
  const [companies, setCompanies] = useState<Company[]>([]);
  const [transportTypes, setTransportTypes] = useState<TransportType[]>([]);
  const [containers, setContainers] = useState<Container[]>([]);
  // Add state to track bookings being processed
  const [processingBookingIds, setProcessingBookingIds] = useState<number[]>(
    [],
  );
  // State for tracking bookings with pending tasks
  const [bookingsWithPendingTasks, setBookingsWithPendingTasks] = useState<
    Set<number>
  >(new Set());
  // Add state for agentFilter and agents
  const [agentFilter, setAgentFilter] = useState<string | null>(
    getUrlParam("agent"),
  );
  const [agents, setAgents] = useState<Agent[]>([]);
  const [bookingTypeFilter, setBookingTypeFilter] = useState<string | null>(
    getUrlParam("bookingType"),
  );
  const [serviceLevelFilter, setServiceLevelFilter] = useState<string | null>(
    getUrlParam("serviceLevel"),
  );
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>([]);
  const [masterContainerFilter, setMasterContainerFilter] = useState<
    string | null
  >(getUrlParam("masterContainer"));
  const [masterContainers, setMasterContainers] = useState<
    Array<{ id: number; reference: string | null }>
  >([]);
  const [invoiceFilter, setInvoiceFilter] = useState<string | null>(
    getUrlParam("invoice"),
  );
  const [deliveryFilter, setDeliveryFilter] = useState<string | null>(
    getUrlParam("delivery"),
  );
  const [sendcloudFilter, setSendcloudFilter] = useState<string | null>(
    getUrlParam("sendcloud"),
  );
  const [leancustomsFilter, setLeancustomsFilter] = useState<string | null>(
    getUrlParam("leancustoms"),
  );
  const [purchaseOrderFilter, setPurchaseOrderFilter] = useState<string | null>(
    getUrlParam("purchaseOrders"),
  );
  // State for multi-selection and bulk operations
  const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([]);
  const [bulkActionDrawerVisible, setBulkActionDrawerVisible] = useState(false);
  const [selectedContainerId, setSelectedContainerId] = useState<string | null>(
    null,
  );
  const [processingBulkAction, setProcessingBulkAction] = useState(false);

  // State for existing bookings in the selected container
  const [existingContainerBookings, setExistingContainerBookings] = useState<
    Booking[]
  >([]);
  const [loadingContainerBookings, setLoadingContainerBookings] =
    useState(false);

  // State for bulk status change
  const [bulkStatusChangeVisible, setBulkStatusChangeVisible] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState<string | null>(
    null,
  );
  const [processingBulkStatus, setProcessingBulkStatus] = useState(false);
  const [downloadingLabels, setDownloadingLabels] = useState(false);
  const [exportingBookings, setExportingBookings] = useState(false);
  const [exportProgress, setExportProgress] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  const [bulkAnalysingCi, setBulkAnalysingCi] = useState(false);
  const [bulkDeepCheckingCi, setBulkDeepCheckingCi] = useState(false);

  // State for SendCloud shipments - map booking ID to shipments with labels
  const [sendcloudShipments, setSendcloudShipments] = useState<
    Map<number, { hasLabel: boolean; count: number }>
  >(new Map());

  // State for column management
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>(
    getSavedColumns(true), // true for admin bookings
  );

  // Function to handle column changes
  const handleColumnsChange = (newColumns: CustomColumn[]) => {
    setCustomColumns(newColumns);
  };

  // Utility function to update URL parameters
  const updateUrlParams = (
    updates: Record<string, string | number | boolean | null | undefined>,
  ) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "all"
      ) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });

    setSearchParams(newParams, { replace: true });
  };

  // Sync filter state changes to URL parameters
  useEffect(() => {
    updateUrlParams({
      search: searchText,
      status:
        statusFilter !== "all" && statusFilter !== BookingStatus.SUBMITTED
          ? statusFilter
          : undefined,
      company: companyFilter,
      transportType: transportTypeFilter,
      bookingType: bookingTypeFilter,
      serviceLevel: serviceLevelFilter,
      dateStart: dateRange?.[0],
      dateEnd: dateRange?.[1],
      agent: agentFilter,
      consolidation: consolidationFilter,
      container: containerFilter,
      masterContainer: masterContainerFilter,
      invoice: invoiceFilter,
      delivery: deliveryFilter,
      sendcloud: sendcloudFilter,
      leancustoms: leancustomsFilter,
      purchaseOrders: purchaseOrderFilter,
      page: pagination.current !== 1 ? pagination.current : undefined,
      pageSize: pagination.pageSize !== 20 ? pagination.pageSize : undefined,
      sortField: sortField,
      sortOrder: sortOrder,
    });
  }, [
    searchText,
    statusFilter,
    companyFilter,
    transportTypeFilter,
    bookingTypeFilter,
    serviceLevelFilter,
    dateRange,
    agentFilter,
    consolidationFilter,
    containerFilter,
    masterContainerFilter,
    invoiceFilter,
    deliveryFilter,
    sendcloudFilter,
    leancustomsFilter,
    purchaseOrderFilter,
    pagination.current,
    pagination.pageSize,
    sortField,
    sortOrder,
  ]);

  // Apply parent-driven status filter (metric cards). Undefined = no override.
  useEffect(() => {
    if (externalStatusFilter === undefined) return;
    setStatusFilter(
      externalStatusFilter === "all" ? "all" : externalStatusFilter,
    );
  }, [externalStatusFilter]);

  // Add effect to handle filter reset
  useEffect(() => {
    if (resetFilters) {
      const statusToKeep =
        externalStatusFilter === "all"
          ? "all"
          : externalStatusFilter && externalStatusFilter !== "all"
            ? externalStatusFilter
            : statusFilter;

      // Reset all filters except status filter (which is handled by externalStatusFilter)
      setSearchText("");
      setSearchQuery("");
      setDateRange(null);
      setCompanyFilter(null);
      setTransportTypeFilter(null);
      setConsolidationFilter(null);
      setContainerFilter(null);
      setAgentFilter(null);
      setBookingTypeFilter(null);
      setServiceLevelFilter(null);
      setMasterContainerFilter(null);
      setInvoiceFilter(null);
      setDeliveryFilter(null);
      setSendcloudFilter(null);
      setLeancustomsFilter(null);
      setPurchaseOrderFilter(null);
      setStatusFilter(statusToKeep);

      // Reset the pagination to first page
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      // Clear URL parameters (except tab, booking_id, and active status filter)
      const newParams = new URLSearchParams(searchParams);
      newParams.forEach((_, key) => {
        if (key !== "tab" && key !== "booking_id" && key !== "status") {
          newParams.delete(key);
        }
      });
      if (statusToKeep && statusToKeep !== "all") {
        newParams.set("status", statusToKeep);
      } else {
        newParams.delete("status");
      }
      setSearchParams(newParams, { replace: true });

      // Manually reload bookings with reset filters but preserve status filter
      loadBookings({
        page: 1,
        status: statusToKeep,
        searchQuery: "",
        dateRange: null,
        companyId: null,
        transportTypeId: null,
        isConsolidation: null,
        containerId: null,
        agentId: null,
        bookingTypeId: null,
        serviceLevel: null,
        masterContainerId: null,
        hasInvoice: null,
        deliveryType: null,
        sendcloudFilter: null,
        leancustomsFilter: null,
        purchaseOrderFilter: null,
      });
    }
  }, [resetFilters]);

  // Initial data fetch
  useEffect(() => {
    loadBookings();
    fetchFilterOptions();
  }, [
    isPendingList,
    statusFilter,
    sortField,
    sortOrder,
    companyFilter,
    transportTypeFilter,
    consolidationFilter,
    containerFilter,
    agentFilter,
    bookingTypeFilter,
    serviceLevelFilter,
    masterContainerFilter,
    invoiceFilter,
    deliveryFilter,
    sendcloudFilter,
    leancustomsFilter,
    purchaseOrderFilter,
    customColumns,
    searchQuery, // Include searchQuery so searches aren't lost when other filters change
  ]);

  // Refresh data when lastUpdate changes (for real-time updates)
  useEffect(() => {
    if (lastUpdate) {
      console.log(
        "BookingsList: lastUpdate changed, reloading bookings at",
        lastUpdate.toISOString(),
      );
      // Force refresh of bookings with current filters
      loadBookings();
    }
  }, [lastUpdate]);

  // Fetch filter options (companies, transport types, containers, agents, booking types)
  const fetchFilterOptions = async () => {
    try {
      // Fetch companies for the filter
      const { data: companiesData, error: companiesError } = await typedSupabase
        .from("company")
        .select("id, name")
        .order("name");

      if (!companiesError && companiesData) {
        setCompanies(companiesData);
      }

      // Fetch transport types for the filter
      const { data: transportTypesData, error: transportTypesError } =
        await typedSupabase
          .from("booking_transport_types")
          .select("id, name, abbreviation")
          .order("name");

      if (!transportTypesError && transportTypesData) {
        setTransportTypes(transportTypesData);
      }

      // Fetch containers for the filter
      const containersResult = await fetchContainers();
      if (!containersResult.error) {
        setContainers(containersResult.data || []);
      }

      // Fetch agents for the filter
      const agentsResult = await fetchAgents();
      if (!agentsResult.error) {
        setAgents(agentsResult.data || []);
      }

      // Fetch booking types for the filter
      const { data: bookingTypesData, error: bookingTypesError } =
        await typedSupabase
          .from("bookings_type")
          .select("id, name")
          .order("name");

      if (!bookingTypesError && bookingTypesData) {
        setBookingTypes(bookingTypesData);
      }

      // Fetch master containers for the filter
      const masterContainersResult = await fetchMasterContainersForFilter();
      if (!masterContainersResult.error) {
        setMasterContainers(masterContainersResult.data || []);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  // Fetch bookings data
  const loadBookings = async (params?: Partial<PaginationParams>) => {
    try {
      setLoading(true);

      // Get visible column keys for query optimization
      const visibleColumnKeys = customColumns
        .filter((col) => col.visible)
        .map((col) => col.key);

      const effectiveStatus =
        params && "status" in params ? params.status : statusFilter;

      const paginationParams: PaginationParams = {
        page: params?.page || pagination.current,
        pageSize: params?.pageSize || pagination.pageSize,
        searchQuery: params?.searchQuery || searchQuery,
        status:
          !effectiveStatus || effectiveStatus === "all"
            ? undefined
            : effectiveStatus,
        dateRange: params?.dateRange || dateRange,
        sortField: params?.sortField || sortField,
        sortOrder: params?.sortOrder || sortOrder,
        companyId: params?.companyId || companyFilter,
        transportTypeId: params?.transportTypeId || transportTypeFilter,
        isConsolidation: params?.isConsolidation ?? consolidationFilter,
        containerId: params?.containerId || containerFilter,
        agentId: params?.agentId || agentFilter,
        bookingTypeId: params?.bookingTypeId || bookingTypeFilter,
        serviceLevel:
          (params?.serviceLevel as ServiceLevel | null | undefined) ??
          (serviceLevelFilter && isServiceLevel(serviceLevelFilter)
            ? serviceLevelFilter
            : null),
        masterContainerId: params?.masterContainerId || masterContainerFilter,
        hasInvoice:
          params?.hasInvoice ??
          (invoiceFilter === "with_invoice"
            ? true
            : invoiceFilter === "no_invoice"
              ? false
              : null),
        deliveryType:
          params?.deliveryType ??
          (deliveryFilter === "storage"
            ? "storage"
            : deliveryFilter === "delivery"
              ? "delivery"
              : null),
        sendcloudFilter:
          params?.sendcloudFilter ??
          (sendcloudFilter === "with_shipments"
            ? "with_shipments"
            : sendcloudFilter === "no_shipments"
              ? "no_shipments"
              : null),
        leancustomsFilter:
          params?.leancustomsFilter ??
          (leancustomsFilter === "with_order_declaration"
            ? "with_order_declaration"
            : leancustomsFilter === "no_order_declaration"
              ? "no_order_declaration"
              : null),
        purchaseOrderFilter:
          params?.purchaseOrderFilter ??
          (purchaseOrderFilter === "with_po"
            ? "with_po"
            : purchaseOrderFilter === "no_po"
              ? "no_po"
              : null),
        visibleColumns: visibleColumnKeys, // Pass visible columns for query optimization
      };

      const result = await fetchBookings(paginationParams);

      if (result.error) {
        console.error("BookingsList: Error fetching bookings:", result.error);
        message.error("Failed to fetch bookings");
        return;
      }

      const bookingsData = result.data || [];
      setBookings(bookingsData);
      setPagination({
        ...pagination,
        current: paginationParams.page,
        pageSize: paginationParams.pageSize,
        total: result.count || 0,
      });

      // Check for pending tasks for each booking
      if (bookingsData.length > 0) {
        checkPendingTasksForBookings(bookingsData);
        fetchSendcloudShipmentsForBookings(bookingsData);
      }

      // Track usage
      trackFeatureUsage(
        isPendingList ? "admin_pending_bookings_view" : "admin_bookings_view",
      );
    } catch (error) {
      console.error("Error loading bookings:", error);
      message.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  // Check for pending tasks for a list of bookings
  const checkPendingTasksForBookings = async (bookingsList: Booking[]) => {
    try {
      const bookingIds = bookingsList.map((booking) => booking.id);

      // Query tasks for all these bookings
      const { data, error } = await typedSupabase
        .from("tasks")
        .select("booking_id, completed")
        .in("booking_id", bookingIds)
        .eq("completed", false);

      if (error) {
        console.error("Error fetching pending tasks:", error);
        return;
      }

      if (data) {
        // Create a set of booking IDs that have pending tasks
        const pendingTaskBookingIds = new Set(
          data.map((task: { booking_id: number }) => Number(task.booking_id)),
        );

        setBookingsWithPendingTasks(pendingTaskBookingIds as Set<number>);
      }
    } catch (error) {
      console.error("Error checking pending tasks:", error);
    }
  };

  // Fetch SendCloud shipments for a list of bookings
  const fetchSendcloudShipmentsForBookings = async (
    bookingsList: Booking[],
  ) => {
    try {
      const bookingIds = bookingsList.map((booking) => booking.id);

      if (bookingIds.length === 0) {
        return;
      }

      // Query SendCloud shipments for all these bookings
      const { data, error } = await typedSupabase
        .from("sendcloud_shipments")
        .select("booking_id, label_url")
        .in("booking_id", bookingIds);

      if (error) {
        console.error("Error fetching SendCloud shipments:", error);
        return;
      }

      if (data) {
        // Create a map of booking ID to shipment info
        const shipmentsMap = new Map<
          number,
          { hasLabel: boolean; count: number }
        >();

        // Group by booking_id
        const groupedByBooking = data.reduce(
          (acc, shipment) => {
            const bookingId = Number(shipment.booking_id);
            if (!acc[bookingId]) {
              acc[bookingId] = { hasLabel: false, count: 0 };
            }
            acc[bookingId].count++;
            if (shipment.label_url) {
              acc[bookingId].hasLabel = true;
            }
            return acc;
          },
          {} as Record<number, { hasLabel: boolean; count: number }>,
        );

        // Convert to Map
        Object.entries(groupedByBooking).forEach(([bookingId, info]) => {
          shipmentsMap.set(Number(bookingId), info);
        });

        setSendcloudShipments(shipmentsMap);
      }
    } catch (error) {
      console.error("Error fetching SendCloud shipments:", error);
    }
  };

  // Handle search input change
  const handleSearch = (value: string) => {
    setSearchText(value);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set a new timeout to debounce the search
    // Just update searchQuery - the effect will handle reloading
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      // Reset to page 1 when search changes
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 500); // 500ms debounce

    searchTimeoutRef.current = timeout;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    // Always default to "all" if value is null or undefined
    const safeValue = value || "all";
    setStatusFilter(safeValue);
    loadBookings({
      page: 1,
      status: safeValue,
    });
  };

  // Handle date range change
  const handleDateRangeChange: RangePickerProps["onChange"] = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const dateRangeValue: [string, string] = [
        dates[0].format("YYYY-MM-DD"),
        dates[1].format("YYYY-MM-DD"),
      ];
      setDateRange(dateRangeValue);
      loadBookings({
        page: 1,
        dateRange: dateRangeValue,
      });
    } else {
      setDateRange(null);
      loadBookings({
        page: 1,
        dateRange: undefined,
      });
    }
  };

  // Handle table change (pagination, filters, sorter)
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Booking> | SorterResult<Booking>[],
  ) => {
    // Handle sorting
    let newSortField: string | undefined;
    let newSortOrder: "ascend" | "descend" | undefined;

    if (!Array.isArray(sorter)) {
      newSortField = sorter.field as string;
      newSortOrder = sorter.order as "ascend" | "descend" | undefined;
    }

    // Set sort state for next query
    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Handle pagination
    loadBookings({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      sortField: newSortField,
      sortOrder: newSortOrder,
    });
  };

  // Handle booking approval
  const handleApproveBooking = async (booking: Booking) => {
    try {
      setLoading(true);
      // Add booking ID to processing list
      setProcessingBookingIds((prev) => [...prev, booking.id]);

      const result = await updateBookingStatus(
        booking.id,
        BookingStatus.APPROVED,
      );

      if (result.error) {
        message.error("Failed to approve booking");
        return;
      }

      message.success(`Booking #${booking.id} approved successfully`);
      loadBookings();
      onRefresh();
      trackFeatureUsage("admin_booking_approved");
    } catch (error) {
      console.error("Error approving booking:", error);
      message.error("Failed to approve booking");
    } finally {
      setLoading(false);
      // Remove booking ID from processing list after a delay to prevent immediate row click
      setTimeout(() => {
        setProcessingBookingIds((prev) =>
          prev.filter((id) => id !== booking.id),
        );
      }, 500);
    }
  };

  // Handle booking rejection
  const handleRejectBooking = async (
    booking: Booking,
    rejectionReason: string,
  ) => {
    try {
      setLoading(true);
      // Add booking ID to processing list
      setProcessingBookingIds((prev) => [...prev, booking.id]);

      const result = await updateBookingStatus(
        booking.id,
        BookingStatus.REJECTED,
        {
          rejection_reason: rejectionReason,
        },
      );

      if (result.error) {
        message.error("Failed to reject booking");
        return;
      }

      message.success(`Booking #${booking.id} rejected successfully`);
      loadBookings();
      onRefresh();
      trackFeatureUsage("admin_booking_rejected");
    } catch (error) {
      console.error("Error rejecting booking:", error);
      message.error("Failed to reject booking");
    } finally {
      setLoading(false);
      // Remove booking ID from processing list after a delay to prevent immediate row click
      setTimeout(() => {
        setProcessingBookingIds((prev) =>
          prev.filter((id) => id !== booking.id),
        );
      }, 500);
    }
  };

  // Track which bookings are generating packing slips
  const [generatingPackingSlips, setGeneratingPackingSlips] = useState<
    Set<number>
  >(new Set());

  // Handle packing list generation and download
  const handleDownloadPackingSlip = async (booking: Booking) => {
    setGeneratingPackingSlips((prev) => new Set(prev).add(booking.id));
    try {
      await generateAndDownloadPackingSlip(booking);
    } catch (error) {
      console.error("Error generating packing list:", error);
      message.error("Failed to generate packing list");
    } finally {
      setGeneratingPackingSlips((prev) => {
        const next = new Set(prev);
        next.delete(booking.id);
        return next;
      });
    }
  };

  // Handle company filter change
  const handleCompanyFilterChange = (value: string | null) => {
    setCompanyFilter(value);
    loadBookings({
      page: 1,
      companyId: value,
    });
  };

  // Handle transport type filter change
  const handleTransportTypeFilterChange = (value: string | null) => {
    setTransportTypeFilter(value);
    loadBookings({
      page: 1,
      transportTypeId: value,
    });
  };

  // Handle consolidation filter change
  const handleConsolidationFilterChange = (value: boolean | null) => {
    setConsolidationFilter(value);
    loadBookings({
      page: 1,
      isConsolidation: value,
    });
  };

  // Handle container filter change
  const handleContainerFilterChange = (value: string | null) => {
    setContainerFilter(value);
    setPagination({ ...pagination, current: 1 });
    loadBookings({
      page: 1,
      containerId: value,
    });
  };

  // Handle agent filter change
  const handleAgentFilterChange = (value: string | null) => {
    setAgentFilter(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadBookings({
      page: 1,
      agentId: value,
    });
  };

  // Handle booking type filter change
  const handleBookingTypeFilterChange = (value: string | null) => {
    setBookingTypeFilter(value);
    loadBookings({
      page: 1,
      bookingTypeId: value,
    });
  };

  // Handle FCL service level filter change
  const handleServiceLevelFilterChange = (value: string | null) => {
    setServiceLevelFilter(value);
    loadBookings({
      page: 1,
      serviceLevel:
        value && isServiceLevel(value) ? value : null,
    });
  };

  // Handle master container filter change
  const handleMasterContainerFilterChange = (value: string | null) => {
    setMasterContainerFilter(value);
    loadBookings({
      page: 1,
      masterContainerId: value,
    });
  };

  // Handle invoice filter change
  const handleInvoiceFilterChange = (value: string | null) => {
    setInvoiceFilter(value);
    loadBookings({
      page: 1,
      hasInvoice:
        value === "with_invoice" ? true : value === "no_invoice" ? false : null,
    });
  };

  const handleDeliveryFilterChange = (value: string | null) => {
    setDeliveryFilter(value);
    loadBookings({
      page: 1,
      deliveryType:
        value === "storage"
          ? "storage"
          : value === "delivery"
            ? "delivery"
            : null,
    });
  };

  const handleSendcloudFilterChange = (value: string | null) => {
    setSendcloudFilter(value);
    loadBookings({
      page: 1,
      sendcloudFilter:
        value === "with_shipments"
          ? "with_shipments"
          : value === "no_shipments"
            ? "no_shipments"
            : null,
    });
  };

  const handleLeancustomsFilterChange = (value: string | null) => {
    setLeancustomsFilter(value);
    loadBookings({
      page: 1,
      leancustomsFilter:
        value === "with_order_declaration"
          ? "with_order_declaration"
          : value === "no_order_declaration"
            ? "no_order_declaration"
            : null,
    });
  };

  const handlePurchaseOrderFilterChange = (value: string | null) => {
    setPurchaseOrderFilter(value);
    loadBookings({
      page: 1,
      purchaseOrderFilter:
        value === "with_po"
          ? "with_po"
          : value === "no_po"
            ? "no_po"
            : null,
    });
  };

  const handleBulkStatusChange = async () => {
    if (!selectedBulkStatus || selectedBookingIds.length === 0) {
      message.error("Please select a status and at least one booking");
      return;
    }

    setProcessingBulkStatus(true);

    try {
      const result = await bulkUpdateBookingStatus(
        selectedBookingIds,
        selectedBulkStatus as BookingStatus,
      );

      if (result.success) {
        message.success(
          `Successfully updated ${selectedBookingIds.length} booking(s) to ${selectedBulkStatus}`,
        );

        // Clear selections and close modal
        setSelectedBookingIds([]);
        setBulkStatusChangeVisible(false);
        setSelectedBulkStatus(null);

        // Reload bookings to reflect changes
        loadBookings();

        // Track the bulk status change event
        trackFeatureUsage("bulk_status_change", {
          booking_count: selectedBookingIds.length,
          new_status: selectedBulkStatus,
        });
      } else {
        throw new Error("Bulk status update failed");
      }
    } catch (error) {
      console.error("Error bulk updating booking statuses:", error);
      message.error("Failed to update booking statuses");
    } finally {
      setProcessingBulkStatus(false);
    }
  };

  // Generate a formatted booking reference
  const getFormattedBookingReference = (booking: Booking) => {
    // Get transport type abbreviation
    const transportInfo = booking.transport_type_info;
    let transportAbbr = "";

    if (transportInfo) {
      if (Array.isArray(transportInfo) && transportInfo.length > 0) {
        transportAbbr = transportInfo[0].abbreviation || "";
      } else if (typeof transportInfo === "object") {
        transportAbbr = (transportInfo as TransportType).abbreviation || "";
      }
    }

    // Format: EFF<booking_id><transport_type.abbreviation>
    return `EFF${booking.id}${transportAbbr}`;
  };

  // Handle row selection change
  const rowSelection = {
    selectedRowKeys: selectedBookingIds,
    preserveSelectedRowKeys: true,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedBookingIds(selectedRowKeys as number[]);
    },
  };

  // Handle adding multiple bookings to a container
  const handleBulkAnalyseCi = async (bookingsToAnalyse: Booking[]) => {
    if (bookingsToAnalyse.length === 0) return;

    const items = bookingsToAnalyse
      .map((booking) => ({
        bookingId: booking.id,
        objectPath: getPrimaryCommercialInvoicePath(
          booking.commercial_invoice_file,
        ),
      }))
      .filter(
        (
          item,
        ): item is { bookingId: number; objectPath: string } =>
          Boolean(item.objectPath),
      );

    if (items.length === 0) {
      message.warning("No commercial invoices found on selected bookings");
      return;
    }

    setBulkAnalysingCi(true);
    const progressKey = "admin-bulk-ci-analysis";

    try {
      message.loading({
        content: `Analysing 0/${items.length} invoices…`,
        key: progressKey,
        duration: 0,
      });

      const result = await bulkAnalyzeStoredCommercialInvoices(items, {
        onProgress: (completed, total) => {
          message.loading({
            content: `Analysing ${completed}/${total} invoices…`,
            key: progressKey,
            duration: 0,
          });
        },
      });

      if (result.succeeded > 0 && result.failed === 0) {
        message.success({
          content: `Analysed ${result.succeeded} commercial invoice${result.succeeded === 1 ? "" : "s"}`,
          key: progressKey,
        });
      } else if (result.succeeded > 0) {
        message.warning({
          content: `Analysed ${result.succeeded}, ${result.failed} failed`,
          key: progressKey,
        });
      } else {
        message.error({
          content: `${result.failed} analysis${result.failed === 1 ? "" : "es"} failed`,
          key: progressKey,
        });
      }

      loadBookings();
      onRefresh();
      trackFeatureUsage("admin_bulk_ci_analyze", { count: result.succeeded });
    } catch (error) {
      console.error("Error bulk analysing commercial invoices:", error);
      message.error({
        content: "Failed to analyse commercial invoices",
        key: progressKey,
      });
    } finally {
      setBulkAnalysingCi(false);
    }
  };

  const handleBulkDeepCheckCi = async (bookingsToCheck: Booking[]) => {
    if (bookingsToCheck.length === 0) return;

    const items = bookingsToCheck
      .map((booking) => ({
        bookingId: booking.id,
        objectPath: getPrimaryCommercialInvoicePath(
          booking.commercial_invoice_file,
        ),
      }))
      .filter(
        (
          item,
        ): item is { bookingId: number; objectPath: string } =>
          Boolean(item.objectPath),
      );

    if (items.length === 0) {
      message.warning("No commercial invoices found on selected bookings");
      return;
    }

    setBulkDeepCheckingCi(true);
    const progressKey = "admin-bulk-ci-deep-check";

    try {
      message.loading({
        content: `AI deep check 0/${items.length}…`,
        key: progressKey,
        duration: 0,
      });

      const result = await bulkAnalyzeStoredCommercialInvoices(items, {
        deepCheck: true,
        onProgress: (completed, total) => {
          message.loading({
            content: `AI deep check ${completed}/${total}…`,
            key: progressKey,
            duration: 0,
          });
        },
      });

      if (result.succeeded > 0 && result.failed === 0) {
        message.success({
          content: `AI deep check completed for ${result.succeeded} invoice${result.succeeded === 1 ? "" : "s"}`,
          key: progressKey,
        });
      } else if (result.succeeded > 0) {
        message.warning({
          content: `AI deep check: ${result.succeeded} succeeded, ${result.failed} failed`,
          key: progressKey,
        });
      } else {
        message.error({
          content: `${result.failed} AI deep check${result.failed === 1 ? "" : "s"} failed`,
          key: progressKey,
        });
      }

      loadBookings();
      onRefresh();
      trackFeatureUsage("admin_bulk_ci_deep_check", {
        count: result.succeeded,
      });
    } catch (error) {
      console.error("Error bulk running AI deep check:", error);
      message.error({
        content: "Failed to run AI deep check",
        key: progressKey,
      });
    } finally {
      setBulkDeepCheckingCi(false);
    }
  };

  const handleAddToContainer = async () => {
    if (!selectedContainerId || selectedBookingIds.length === 0) {
      message.error("Please select a container and at least one booking");
      return;
    }

    try {
      setProcessingBulkAction(true);
      const containerIdNumber = parseInt(selectedContainerId);

      const result = await linkMultipleBookingsToContainer(
        selectedBookingIds,
        containerIdNumber,
      );

      if (result.error) {
        message.error("Failed to add bookings to container");
        return;
      }

      message.success(
        `${selectedBookingIds.length} bookings added to container successfully`,
      );

      // Reset selection and refresh
      setSelectedBookingIds([]);
      setBulkActionDrawerVisible(false);
      setSelectedContainerId(null);

      // Refresh the bookings list
      loadBookings();
      onRefresh();

      trackFeatureUsage("admin_bulk_add_to_container", {
        count: selectedBookingIds.length,
      });
    } catch (error) {
      console.error("Error performing bulk add to container:", error);
      message.error("Failed to process bulk action");
    } finally {
      setProcessingBulkAction(false);
    }
  };

  // Fetch bookings already in the selected container when container selection changes
  useEffect(() => {
    const fetchExistingBookings = async () => {
      if (!selectedContainerId || !bulkActionDrawerVisible) {
        return;
      }

      try {
        setLoadingContainerBookings(true);
        const result = await fetchBookings({
          containerId: selectedContainerId,
        });

        if (!result.error) {
          // Filter out bookings that are already selected
          const uniqueBookings = result.data.filter(
            (booking) => !selectedBookingIds.includes(booking.id),
          );
          setExistingContainerBookings(uniqueBookings);
        }
      } catch (error) {
        console.error("Error fetching container bookings:", error);
      } finally {
        setLoadingContainerBookings(false);
      }
    };

    fetchExistingBookings();
  }, [selectedContainerId, selectedBookingIds, bulkActionDrawerVisible]);

  // Base table columns for bookings table
  const baseColumns: ColumnsType<Booking> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 90,
      sorter: true,
      render: (id, record) => {
        // Get formatted reference
        const reference = getFormattedBookingReference(record);

        // Split the reference to isolate the numeric ID part
        // Format is typically EFF<numeric_id><transport_abbreviation>
        // Extract the components
        const prefix = "EFF";
        const numericId = String(id);
        const suffix = reference.substring(prefix.length + numericId.length);

        // Check if this booking has pending tasks
        const hasPendingTasks = bookingsWithPendingTasks.has(id);

        return (
          <div>
            <div>
              {hasPendingTasks && (
                <Tooltip title="Has pending tasks">
                  <Badge
                    status="processing"
                    color="orange"
                    style={{ marginRight: 4 }}
                  />
                </Tooltip>
              )}
              <span style={{ fontSize: "11px" }}>{prefix}</span>
              <span style={{ fontWeight: 700, fontSize: "14px" }}>
                {numericId}
              </span>
              <span style={{ fontSize: "11px" }}>{suffix}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      sorter: true,
      width: 150,
      render: (reference) =>
        reference ? (
          <Tooltip title={reference}>
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 130,
              }}
            >
              {reference}
            </div>
          </Tooltip>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "Company",
      key: "company",
      width: 180,
      render: (_, record) => {
        // Handle both object and array formats
        const companyInfo = record.company_info;
        let companyName = "N/A";

        if (companyInfo) {
          if (Array.isArray(companyInfo) && companyInfo.length > 0) {
            companyName = companyInfo[0].name || "N/A";
          } else if (typeof companyInfo === "object") {
            companyName = (companyInfo as Company).name || "N/A";
          }
        }

        return (
          <Tooltip title={companyName}>
            <span>{companyName}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      sorter: true,
      render: (status) => {
        const color = getStatusColor(status);
        let displayText = status || "unknown";

        // Make first letter uppercase
        displayText =
          displayText.charAt(0).toUpperCase() + displayText.slice(1);

        // Special case for 'submitted' status
        if (status === BookingStatus.SUBMITTED) {
          return <Tag color={color}>Pending Approval</Tag>;
        }

        return <Tag color={color}>{displayText}</Tag>;
      },
    },
    {
      title: "Transport",
      key: "transport_type",
      width: 80,
      render: (_, record) => {
        const transportInfo = record.transport_type_info;
        let transportName = "N/A";
        let transportAbbr = "";

        if (transportInfo) {
          if (Array.isArray(transportInfo) && transportInfo.length > 0) {
            transportName = transportInfo[0].name || "N/A";
            transportAbbr = transportInfo[0].abbreviation || "";
          } else if (typeof transportInfo === "object") {
            transportName = (transportInfo as TransportType).name || "N/A";
            transportAbbr = (transportInfo as TransportType).abbreviation || "";
          }
        }

        // Display emoji based on transport type
        let emoji = "";
        if (transportAbbr === "AIR") {
          emoji = "✈️";
        } else if (transportAbbr === "SEA") {
          emoji = "🚢";
        }

        return (
          <Tooltip title={transportName}>
            <span
              style={{
                fontSize: "18px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {emoji || "-"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Booking Type",
      key: "booking_type",
      width: 130,
      render: (_, record) => {
        const bookingTypeInfo = record.booking_type_info;
        let bookingTypeName = "-";

        if (bookingTypeInfo) {
          if (Array.isArray(bookingTypeInfo) && bookingTypeInfo.length > 0) {
            bookingTypeName = bookingTypeInfo[0].name || "-";
          } else if (typeof bookingTypeInfo === "object") {
            bookingTypeName = (bookingTypeInfo as BookingType).name || "-";
          }
        }

        return (
          <Tooltip title={bookingTypeName}>
            <span>{bookingTypeName}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Service Level",
      key: "service_level",
      width: 120,
      render: (_, record) => {
        if (record.service_level && isServiceLevel(record.service_level)) {
          return (
            <Tag color={record.service_level === "premium" ? "gold" : "default"}>
              {SERVICE_LEVEL_LABELS[record.service_level]}
            </Tag>
          );
        }
        return <span>-</span>;
      },
    },
    {
      title: "🪣 Origin",
      key: "origin",
      width: 150,
      render: (_, record) => {
        // Get origin info
        const originInfo = record.origin_port_info;
        let originName = "-";

        if (originInfo) {
          if (Array.isArray(originInfo) && originInfo.length > 0) {
            originName = originInfo[0].name || "-";
          } else if (typeof originInfo === "object") {
            originName = (originInfo as Port).name || "-";
          }
        }

        // Check for container origin
        const containerInfo = record.container_info;
        let containerOrigin = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.container_origin_port) {
            const containerOriginPort = Array.isArray(
              container.container_origin_port,
            )
              ? container.container_origin_port[0]
              : container.container_origin_port;

            if (containerOriginPort && containerOriginPort.name) {
              containerOrigin = containerOriginPort.name;
            }
          }
        }

        // If there's container origin, display it
        if (containerOrigin) {
          return <span>{containerOrigin}</span>;
        }

        return <span>{originName}</span>;
      },
    },
    {
      title: "🪣 Destination",
      key: "destination",
      width: 150,
      render: (_, record) => {
        // Get destination info
        const destInfo = record.destination_port_info;
        let destName = "-";

        if (destInfo) {
          if (Array.isArray(destInfo) && destInfo.length > 0) {
            destName = destInfo[0].name || "-";
          } else if (typeof destInfo === "object") {
            destName = (destInfo as Port).name || "-";
          }
        }

        // Check for container destination
        const containerInfo = record.container_info;
        let containerDest = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.container_destination_port) {
            const containerDestPort = Array.isArray(
              container.container_destination_port,
            )
              ? container.container_destination_port[0]
              : container.container_destination_port;

            if (containerDestPort && containerDestPort.name) {
              containerDest = containerDestPort.name;
            }
          }
        }

        // If there's container destination, display it
        if (containerDest) {
          return <span>{containerDest}</span>;
        }

        return <span>{destName}</span>;
      },
    },
    {
      title: "🪣 ETD",
      key: "etd",
      width: 140,
      render: (_, record) => {
        const containerInfo = record.container_info;
        let container: { etd?: string | null; delayed_etd?: string | null } | null =
          null;

        if (containerInfo) {
          container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;
        }

        const effectiveEtd = container?.delayed_etd || container?.etd;
        if (!effectiveEtd) {
          return <span>-</span>;
        }

        return (
          <span>
            {formatDate(effectiveEtd)}
            {container?.delayed_etd ? (
              <Tag color="orange" style={{ marginLeft: 6 }}>
                Delayed
              </Tag>
            ) : null}
          </span>
        );
      },
    },
    {
      title: "🪣 Customer ETA",
      dataIndex: "eta",
      key: "eta",
      sorter: true,
      width: 140,
      render: (date, record) => {
        // Priority: booking.eta (override) > container.eta_customer > "-"
        if (date) {
          // booking.eta is set (override)
          return (
            <span>
              {formatDate(date)} <Tag color="orange">Override</Tag>
            </span>
          );
        }

        // Check for container customer ETA
        const containerInfo = record.container_info;
        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.eta_customer) {
            return <span>{formatDate(container.eta_customer)}</span>;
          }
        }

        return "-";
      },
    },
    {
      title: "Container",
      key: "container",
      width: 120,
      render: (_, record) => {
        // Get container info
        const containerInfo = record.container_info;
        let containerReference = "-";
        let containerId = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container) {
            containerId = container.id;
            if (container.reference) {
              containerReference = container.reference;
            } else if (container.id) {
              containerReference = `#${container.id}`;
            }
          }
        }

        // If there's no container, return a simple dash
        if (containerReference === "-" || !containerId) {
          return <span>{containerReference}</span>;
        }

        // Otherwise, render a clickable link
        return (
          <Tooltip title="View container details">
            <a
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering row click (booking details)
                window.open(`/admin/containers/${containerId}`, "_blank");
              }}
              style={{ cursor: "pointer", color: "#1890ff" }}
            >
              {containerReference}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: "⭐ Bill of lading (B/L)",
      key: "master_container",
      width: 140,
      render: (_, record) => {
        // Get container info first
        const containerInfo = record.container_info;
        let masterContainerInfo = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.master_container_info) {
            const masterContainerData = Array.isArray(
              container.master_container_info,
            )
              ? container.master_container_info[0]
              : container.master_container_info;

            if (masterContainerData && masterContainerData.master_containers) {
              const masterContainer = Array.isArray(
                masterContainerData.master_containers,
              )
                ? masterContainerData.master_containers[0]
                : masterContainerData.master_containers;

              if (masterContainer) {
                masterContainerInfo = {
                  id: masterContainer.id,
                  reference: masterContainer.reference,
                };
              }
            }
          }
        }

        // If there's no master container, return a dash
        if (!masterContainerInfo) {
          return <span>-</span>;
        }

        // Otherwise, render a clickable link
        return (
          <Tooltip title="View bill of lading (B/L)">
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering row click (booking details)
                window.open(
                  `/admin/containers?tab=master-containers&master_id=${masterContainerInfo.id}`,
                  "_blank",
                );
              }}
              style={{ padding: 0, height: "auto" }}
            >
              {masterContainerInfo.reference || `#${masterContainerInfo.id}`}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: "Products",
      key: "products",
      width: 90,
      render: (_, record) => {
        const productInfo = record.product_info;
        let productCount = 0;

        if (productInfo) {
          if (Array.isArray(productInfo)) {
            productCount = productInfo.length;
          }
        }

        return <span>{productCount > 0 ? productCount : "-"}</span>;
      },
    },
    {
      title: "Delivery",
      key: "delivery",
      width: 100,
      render: (_, record) => {
        // Get delivery info
        const deliveryInfo = record.booking_delivery_info;

        // Check if delivery info exists and has valid data
        const hasDelivery =
          deliveryInfo &&
          ((Array.isArray(deliveryInfo) && deliveryInfo.length > 0) ||
            (typeof deliveryInfo === "object" &&
              !Array.isArray(deliveryInfo) &&
              deliveryInfo !== null));

        // If no delivery record exists, show storage icon
        if (!hasDelivery) {
          const storageTooltip =
            record.storage_option &&
            isStorageOption(record.storage_option)
              ? STORAGE_OPTION_LABELS[record.storage_option]
              : "In opslag (storage)";

          return (
            <Tooltip title={storageTooltip}>
              <InboxOutlined
                style={{
                  color: "#666",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          );
        }

        // Get the delivery data
        const deliveryData = Array.isArray(deliveryInfo)
          ? deliveryInfo[0]
          : deliveryInfo;

        // Format full address
        const formatAddress = (delivery: any) => {
          const parts = [];
          if (delivery.street_name) parts.push(delivery.street_name);
          if (delivery.house_number) parts.push(delivery.house_number);
          if (delivery.city) parts.push(delivery.city);
          if (delivery.postal_code) parts.push(delivery.postal_code);
          if (delivery.country) parts.push(delivery.country);
          return parts.join(", ");
        };

        const fullAddress = formatAddress(deliveryData);

        return (
          <Tooltip
            title={fullAddress || "Delivery address"}
            placement="topLeft"
          >
            <HomeOutlined
              style={{
                color: "#1890ff",
                fontSize: "16px",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Storage Option",
      key: "storage_option",
      width: 220,
      render: (_, record) => {
        if (!isStorageBooking(record)) {
          return <span>-</span>;
        }

        if (record.storage_option && isStorageOption(record.storage_option)) {
          return (
            <Tag color="purple" style={{ whiteSpace: "normal" }}>
              {STORAGE_OPTION_LABELS[record.storage_option]}
            </Tag>
          );
        }

        return <span>-</span>;
      },
    },
    {
      title: "Postal code",
      key: "postal_code",
      width: 110,
      render: (_, record) => {
        const deliveryInfo = record.booking_delivery_info;
        const deliveryData = deliveryInfo
          ? Array.isArray(deliveryInfo)
            ? deliveryInfo[0]
            : deliveryInfo
          : null;
        return (
          <span>{(deliveryData as DeliveryInfo)?.postal_code || "-"}</span>
        );
      },
    },
    {
      title: "City",
      key: "city",
      width: 140,
      render: (_, record) => {
        const deliveryInfo = record.booking_delivery_info;
        const deliveryData = deliveryInfo
          ? Array.isArray(deliveryInfo)
            ? deliveryInfo[0]
            : deliveryInfo
          : null;
        return <span>{(deliveryData as DeliveryInfo)?.city || "-"}</span>;
      },
    },
    {
      title: "📦 SendCloud",
      key: "sendcloud",
      width: 120,
      render: (_, record) => {
        const shipmentInfo = sendcloudShipments.get(record.id);

        if (!shipmentInfo || shipmentInfo.count === 0) {
          return <span>-</span>;
        }

        const hasLabel = shipmentInfo.hasLabel;
        const count = shipmentInfo.count;

        return (
          <Tooltip
            title={
              hasLabel
                ? `${count} shipment(s) with label(s) - Click to download`
                : `${count} shipment(s) without label`
            }
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                cursor: hasLabel ? "pointer" : "default",
                color: hasLabel ? "#1890ff" : "#666",
              }}
              onClick={(e) => {
                if (hasLabel) {
                  e.stopPropagation();
                  onViewDetails(record, "4"); // Open Delivery Information tab (tab 4)
                }
              }}
            >
              <InboxOutlined
                style={{
                  fontSize: "16px",
                  color: hasLabel ? "#52c41a" : "#d9d9d9",
                }}
              />
              {hasLabel && (
                <Badge
                  count={count}
                  style={{ backgroundColor: "#52c41a" }}
                  title={`${count} shipment(s) with label(s)`}
                />
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "📋 Lean Customs",
      key: "leancustoms",
      width: 140,
      render: (_, record) => {
        const orderId = record.leancustoms_order_id;
        const declId = record.leancustoms_declaration_id;
        if (!orderId && !declId) return <span>-</span>;
        return (
          <Space size="small" wrap onClick={(e) => e.stopPropagation()}>
            {orderId && (
              <a
                href={`${LEANCUSTOMS_ORDER_BASE}/${orderId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkOutlined /> Order
              </a>
            )}
            {declId && (
              <a
                href={`${LEANCUSTOMS_DECLARATION_BASE}/${declId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkOutlined /> Declaration
              </a>
            )}
          </Space>
        );
      },
    },
    {
      title: "🛒 POs",
      key: "purchase_orders",
      width: 100,
      align: "center",
      render: (_, record) => {
        const po = record.booking_purchase_orders;
        const count = Array.isArray(po) ? po.length : 0;
        if (count === 0) {
          return <span>-</span>;
        }
        return (
          <Tooltip
            title={`${count} purchase order${count === 1 ? "" : "s"} created (WMS)`}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#52c41a",
              }}
            >
              <ShoppingCartOutlined style={{ fontSize: 16 }} />
              <Badge
                count={count}
                style={{ backgroundColor: "#52c41a" }}
                overflowCount={99}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Agent",
      key: "agent",
      width: 140,
      render: (_, record) => {
        // Handle both object and array formats
        const agentInfo = record.agent_info;
        let agentName = "";

        if (agentInfo) {
          if (Array.isArray(agentInfo) && agentInfo.length > 0) {
            agentName = agentInfo[0].name || "";
          } else if (typeof agentInfo === "object") {
            agentName = (agentInfo as Agent).name || "";
          }
        }

        return <span>{agentName || "-"}</span>;
      },
    },
    {
      title: "CI",
      key: "ci_validity",
      width: 64,
      align: "center",
      sorter: (a, b) => {
        const score = (row: Booking) => {
          const path = row.commercial_invoice_file?.split(",")[0]?.trim();
          if (!path) return -1;
          const analysis = Array.isArray(row.commercial_invoice_analysis)
            ? row.commercial_invoice_analysis[0]
            : row.commercial_invoice_analysis;
          if (!analysis || analysis.object_path !== path) return -1;
          return analysis.likelihood_score;
        };
        return score(a) - score(b);
      },
      render: (_, record) => <AgentCiValidityCell booking={record} />,
    },
    {
      title: "📄 Invoice",
      key: "invoice",
      width: 120,
      render: (_, record) => {
        // Handle invoice info
        const invoiceInfo = record.invoice_info;
        let hasInvoice = false;
        let invoiceData = null;

        if (invoiceInfo) {
          if (Array.isArray(invoiceInfo) && invoiceInfo.length > 0) {
            hasInvoice = true;
            invoiceData = invoiceInfo[0];
          } else if (typeof invoiceInfo === "object") {
            hasInvoice = true;
            invoiceData = invoiceInfo;
          }
        }

        if (!hasInvoice) {
          return (
            <Button
              type="link"
              size="small"
              style={{
                padding: 0,
                height: "auto",
                color: "#1890ff",
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Navigate to invoices tab with booking ID for creation
                navigate(`/admin/invoices?create_from_booking=${record.id}`);
              }}
            >
              Create Invoice
            </Button>
          );
        }

        // Get payment status color
        const getPaymentStatusColor = (status: string | null | undefined) => {
          switch (status?.toLowerCase()) {
            case "paid":
              return "#52c41a"; // green
            case "open":
              return "#faad14"; // orange
            case "overdue":
              return "#ff4d4f"; // red
            case "partial":
              return "#1890ff"; // blue
            default:
              return "#d9d9d9"; // gray
          }
        };

        const paymentStatus =
          (invoiceData as InvoiceInfo)?.moneybird_payment_status || "unknown";
        const statusColor = getPaymentStatusColor(paymentStatus);

        // Calculate total amount from invoice lines
        const calculateTotal = (lines: InvoiceLine[]) => {
          if (!lines || !Array.isArray(lines)) return 0;
          return lines.reduce((total, line) => {
            const lineTotal = (line.quantity || 0) * (line.unit_price || 0);
            const vatAmount = lineTotal * ((line.vat_percentage || 0) / 100);
            return total + lineTotal + vatAmount;
          }, 0);
        };

        const totalAmount = calculateTotal(
          (invoiceData as InvoiceInfo)?.lines || [],
        );

        const invoice = invoiceData as InvoiceInfo;
        const lines = invoice?.lines || [];

        // Create detailed tooltip content
        const tooltipContent = (
          <div style={{ maxWidth: 300 }}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>
              Invoice #{invoice?.id}
            </div>

            <div style={{ marginBottom: 4 }}>
              <strong>Total:</strong> {invoice?.currency}{" "}
              {totalAmount.toFixed(2)}
            </div>

            <div style={{ marginBottom: 4 }}>
              <strong>Status:</strong>{" "}
              <span style={{ color: statusColor, fontWeight: "bold" }}>
                {paymentStatus}
              </span>
            </div>

            <div style={{ marginBottom: 4 }}>
              <strong>Created:</strong>{" "}
              {invoice?.created_at
                ? new Date(invoice.created_at).toLocaleDateString()
                : "N/A"}
            </div>

            {invoice?.moneybird_invoice_date && (
              <div style={{ marginBottom: 4 }}>
                <strong>Invoice Date:</strong>{" "}
                {new Date(invoice.moneybird_invoice_date).toLocaleDateString()}
              </div>
            )}

            <div style={{ marginBottom: 8 }}>
              <strong>Lines:</strong> {lines.length} item
              {lines.length !== 1 ? "s" : ""}
            </div>

            {lines.length > 0 && (
              <div style={{ fontSize: "11px", color: "#666" }}>
                <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                  Breakdown:
                </div>
                {lines.slice(0, 3).map((line, index) => {
                  const lineTotal =
                    (line.quantity || 0) * (line.unit_price || 0);
                  const vatAmount =
                    lineTotal * ((line.vat_percentage || 0) / 100);
                  const lineTotalWithVat = lineTotal + vatAmount;

                  return (
                    <div key={index} style={{ marginBottom: 2 }}>
                      {line.quantity}x @ {invoice?.currency}{" "}
                      {(line.unit_price || 0).toFixed(2)} = {invoice?.currency}{" "}
                      {lineTotalWithVat.toFixed(2)}
                      {line.vat_percentage > 0 && (
                        <span style={{ color: "#888" }}>
                          {" "}
                          (inc. {line.vat_percentage}% VAT)
                        </span>
                      )}
                    </div>
                  );
                })}
                {lines.length > 3 && (
                  <div style={{ color: "#888", fontStyle: "italic" }}>
                    ... and {lines.length - 3} more line
                    {lines.length - 3 !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 8, fontSize: "11px", color: "#666" }}>
              Click to view in Invoices tab
            </div>
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            overlayStyle={{ maxWidth: 350 }}
            trigger="hover"
            mouseEnterDelay={0.3}
            mouseLeaveDelay={0.1}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Navigate to invoices tab and show this specific invoice
                navigate(`/admin/invoices?invoice_id=${invoice?.id}`);
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: statusColor,
                }}
              />
              <span>#{invoice?.id}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Generate & Download Packing List">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              loading={generatingPackingSlips.has(record.id)}
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click
                handleDownloadPackingSlip(record);
              }}
            />
          </Tooltip>
          {record.status === BookingStatus.SUBMITTED && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="text"
                  style={{ color: "#52c41a" }}
                  icon={<CheckCircleOutlined />}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    handleApproveBooking(record);
                  }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="text"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    Modal.confirm({
                      title: "Reject Booking",
                      content: (
                        <div>
                          <p>Are you sure you want to reject this booking?</p>
                          <AntInput.TextArea
                            placeholder="Reason for rejection"
                            rows={3}
                            id="rejection-reason"
                          />
                        </div>
                      ),
                      onOk: () => {
                        const reasonElement = document.getElementById(
                          "rejection-reason",
                        ) as HTMLTextAreaElement;
                        const reason = reasonElement?.value || "";
                        handleRejectBooking(record, reason);
                      },
                    });
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Apply column visibility and ordering based on user preferences
  const columns: ColumnsType<Booking> = applyColumnVisibility(
    baseColumns,
    customColumns,
  );

  const handleExportBookings = async () => {
    try {
      setExportingBookings(true);
      setExportProgress({ loaded: 0, total: 0 });
      const exportingSelected = selectedBookingIds.length > 0;
      const shared = {
        page: 1,
        pageSize: 1000,
        fetchAll: true,
        selectAllRelations: true,
        onExportProgress: (loaded: number, total: number) => {
          setExportProgress({ loaded, total });
        },
      };
      const result = await fetchBookings(
        exportingSelected
          ? { ...shared, bookingIds: selectedBookingIds }
          : {
              ...shared,
              searchQuery: searchQuery || undefined,
              status:
                !statusFilter || statusFilter === "all"
                  ? undefined
                  : statusFilter,
              dateRange,
              sortField,
              sortOrder,
              companyId: companyFilter,
              transportTypeId: transportTypeFilter,
              isConsolidation: consolidationFilter,
              containerId: containerFilter,
              agentId: agentFilter,
              bookingTypeId: bookingTypeFilter,
              serviceLevel:
                serviceLevelFilter && isServiceLevel(serviceLevelFilter)
                  ? serviceLevelFilter
                  : null,
              masterContainerId: masterContainerFilter,
              hasInvoice:
                invoiceFilter === "with_invoice"
                  ? true
                  : invoiceFilter === "no_invoice"
                    ? false
                    : null,
              deliveryType:
                deliveryFilter === "storage"
                  ? "storage"
                  : deliveryFilter === "delivery"
                    ? "delivery"
                    : null,
              sendcloudFilter:
                sendcloudFilter === "with_shipments"
                  ? "with_shipments"
                  : sendcloudFilter === "no_shipments"
                    ? "no_shipments"
                    : null,
              leancustomsFilter:
                leancustomsFilter === "with_order_declaration"
                  ? "with_order_declaration"
                  : leancustomsFilter === "no_order_declaration"
                    ? "no_order_declaration"
                    : null,
              purchaseOrderFilter:
                purchaseOrderFilter === "with_po"
                  ? "with_po"
                  : purchaseOrderFilter === "no_po"
                    ? "no_po"
                    : null,
            },
      );

      if (result.error) {
        message.error("Failed to export bookings");
        return;
      }

      const rows = result.data || [];
      if (rows.length === 0) {
        message.warning("No bookings to export");
        return;
      }

      downloadAdminBookingsCsv(
        rows,
        `bookings-${dayjs().format("YYYY-MM-DD")}.csv`,
      );
      message.success(`Exported ${rows.length} bookings`);
    } catch (error) {
      console.error("Error exporting bookings:", error);
      message.error("Failed to export bookings");
    } finally {
      setExportingBookings(false);
      setExportProgress(null);
    }
  };

  // Render filtering controls
  const renderFilters = () => {
    const filterStyle = (min: number, max: number): React.CSSProperties => ({
      flex: `1 1 ${min}px`,
      minWidth: min,
      maxWidth: max,
    });

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
          <div style={filterStyle(180, 260)}>
            <Input
              placeholder="Search ID or reference..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              size="middle"
            />
          </div>
          <div style={filterStyle(150, 200)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              allowClear
              size="middle"
            >
              <Option value="all">All Statuses</Option>
              <Option value={BookingStatus.WAITING_FOR_GOODS}>
                {bookingStatusLabels[BookingStatus.WAITING_FOR_GOODS]}
              </Option>
              <Option value={BookingStatus.CANCELLED}>
                {bookingStatusLabels[BookingStatus.CANCELLED]}
              </Option>
              <Option value={BookingStatus.DELIVERY_PLANNED}>
                {bookingStatusLabels[BookingStatus.DELIVERY_PLANNED]}
              </Option>
              <Option value={BookingStatus.OUT_FOR_DELIVERY}>
                {bookingStatusLabels[BookingStatus.OUT_FOR_DELIVERY]}
              </Option>
              <Option value={BookingStatus.CONCEPT}>
                {bookingStatusLabels[BookingStatus.CONCEPT]}
              </Option>
              <Option value={BookingStatus.ADDED_TO_STOCK}>
                {bookingStatusLabels[BookingStatus.ADDED_TO_STOCK]}
              </Option>
              <Option value={BookingStatus.GOODS_RECEIVED_ORIGIN}>
                {bookingStatusLabels[BookingStatus.GOODS_RECEIVED_ORIGIN]}
              </Option>
              <Option value={BookingStatus.APPROVED}>
                {bookingStatusLabels[BookingStatus.APPROVED]}
              </Option>
              <Option value={BookingStatus.IN_TRANSIT}>
                {bookingStatusLabels[BookingStatus.IN_TRANSIT]}
              </Option>
              <Option value={BookingStatus.CUSTOMS_PENDING_CLEARANCE}>
                {bookingStatusLabels[BookingStatus.CUSTOMS_PENDING_CLEARANCE]}
              </Option>
              <Option value={BookingStatus.CUSTOMS_CLEARED}>
                {bookingStatusLabels[BookingStatus.CUSTOMS_CLEARED]}
              </Option>
              <Option value={BookingStatus.FYCO}>
                {bookingStatusLabels[BookingStatus.FYCO]}
              </Option>
              <Option value={BookingStatus.ARCHIVED}>
                {bookingStatusLabels[BookingStatus.ARCHIVED]}
              </Option>
              <Option value={BookingStatus.REJECTED}>
                {bookingStatusLabels[BookingStatus.REJECTED]}
              </Option>
              <Option value={BookingStatus.DELIVERED}>
                {bookingStatusLabels[BookingStatus.DELIVERED]}
              </Option>
            </Select>
          </div>
          <div style={filterStyle(170, 240)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Company"
              value={companyFilter}
              onChange={handleCompanyFilterChange}
              allowClear
              showSearch
              optionFilterProp="children"
              size="middle"
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id.toString()}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </div>
          <div style={filterStyle(140, 190)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Transport"
              value={transportTypeFilter}
              onChange={handleTransportTypeFilterChange}
              allowClear
              size="middle"
            >
              {transportTypes.map((type) => (
                <Option key={type.id} value={type.id.toString()}>
                  {type.abbreviation === "AIR"
                    ? "✈️"
                    : type.abbreviation === "SEA"
                      ? "🚢"
                      : ""}{" "}
                  {type.name}
                </Option>
              ))}
            </Select>
          </div>
          <div style={filterStyle(150, 200)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Booking Type"
              value={bookingTypeFilter}
              onChange={handleBookingTypeFilterChange}
              allowClear
              size="middle"
              showSearch
              optionFilterProp="children"
            >
              {bookingTypes.map((type) => (
                <Option key={type.id} value={type.id.toString()}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </div>
          <div style={filterStyle(140, 180)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Service Level"
              value={serviceLevelFilter}
              onChange={handleServiceLevelFilterChange}
              allowClear
              size="middle"
            >
              {SERVICE_LEVEL_VALUES.map((value) => (
                <Option key={value} value={value}>
                  {SERVICE_LEVEL_LABELS[value]}
                </Option>
              ))}
            </Select>
          </div>
          <div style={filterStyle(220, 280)}>
            <RangePicker
              style={{ width: "100%" }}
              value={
                dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null
              }
              onChange={handleDateRangeChange}
              placeholder={["Start", "End"]}
              size="middle"
            />
          </div>
          <div style={filterStyle(150, 200)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Agent"
              value={agentFilter}
              onChange={handleAgentFilterChange}
              allowClear
              showSearch
              optionFilterProp="children"
              size="middle"
            >
              {agents.map((agent) => (
                <Option key={agent.id} value={agent.id.toString()}>
                  {agent.name}
                </Option>
              ))}
            </Select>
          </div>
          <div style={filterStyle(160, 200)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Consolidation"
              value={
                consolidationFilter === null
                  ? undefined
                  : consolidationFilter.toString()
              }
              onChange={(value) =>
                handleConsolidationFilterChange(
                  value === undefined ? null : value === "true",
                )
              }
              allowClear
              size="middle"
            >
              <Option value="true">Consolidated</Option>
              <Option value="false">Not Consolidated</Option>
            </Select>
          </div>
          <div style={filterStyle(200, 280)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Container"
              value={containerFilter}
              onChange={handleContainerFilterChange}
              allowClear
              showSearch
              optionFilterProp="children"
              size="middle"
            >
              {containers.map((container) => {
                const originPort = container.container_origin_port
                  ? Array.isArray(container.container_origin_port)
                    ? container.container_origin_port[0]?.name
                    : container.container_origin_port.name
                  : null;

                const destPort = container.container_destination_port
                  ? Array.isArray(container.container_destination_port)
                    ? container.container_destination_port[0]?.name
                    : container.container_destination_port.name
                  : null;

                const displayText = `${container.reference || container.id} ${
                  originPort ? `(${originPort}` : ""
                }${destPort ? ` → ${destPort})` : originPort ? ")" : ""}`;

                return (
                  <Option key={container.id} value={container.id.toString()}>
                    🚢 {displayText}
                  </Option>
                );
              })}
            </Select>
          </div>
          <div style={filterStyle(180, 240)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Bill of lading (B/L)"
              value={masterContainerFilter}
              onChange={handleMasterContainerFilterChange}
              allowClear
              showSearch
              optionFilterProp="children"
              size="middle"
            >
              {masterContainers.map((masterContainer) => (
                <Option
                  key={masterContainer.id}
                  value={masterContainer.id.toString()}
                >
                  {masterContainer.reference || `#${masterContainer.id}`}
                </Option>
              ))}
            </Select>
          </div>
          <div style={filterStyle(140, 180)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Invoice"
              value={invoiceFilter}
              onChange={handleInvoiceFilterChange}
              allowClear
              size="middle"
            >
              <Option value="with_invoice">With Invoice</Option>
              <Option value="no_invoice">No Invoice</Option>
            </Select>
          </div>
          <div style={filterStyle(140, 180)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Delivery"
              value={deliveryFilter}
              onChange={handleDeliveryFilterChange}
              allowClear
              size="middle"
            >
              <Option value="delivery">For Delivery</Option>
              <Option value="storage">In Storage</Option>
            </Select>
          </div>
          <div style={filterStyle(150, 190)}>
            <Select
              style={{ width: "100%" }}
              placeholder="SendCloud"
              value={sendcloudFilter}
              onChange={handleSendcloudFilterChange}
              allowClear
              size="middle"
            >
              <Option value="with_shipments">Has Shipments</Option>
              <Option value="no_shipments">No Shipments</Option>
            </Select>
          </div>
          <div style={filterStyle(170, 220)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Lean Customs"
              value={leancustomsFilter}
              onChange={handleLeancustomsFilterChange}
              allowClear
              size="middle"
            >
              <Option value="with_order_declaration">
                Has Order/Declaration
              </Option>
              <Option value="no_order_declaration">No Order/Declaration</Option>
            </Select>
          </div>
          <div style={filterStyle(160, 200)}>
            <Select
              style={{ width: "100%" }}
              placeholder="Purchase orders"
              value={purchaseOrderFilter}
              onChange={handlePurchaseOrderFilterChange}
              allowClear
              size="middle"
            >
              <Option value="with_po">Has PO(s)</Option>
              <Option value="no_po">No PO</Option>
            </Select>
          </div>
          <div
            style={{
              display: "flex",
              flex: "0 0 auto",
              alignItems: "center",
              gap: 8,
              marginLeft: "auto",
            }}
          >
            <BookingTableColumnManager
              isAdmin={true}
              onColumnsChange={handleColumnsChange}
            />
            <Tooltip title="Exporteert alle kolommen, ook de kolommen die uit staan in Kolommen Aanpassen">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportBookings}
              loading={exportingBookings}
              size="middle"
            >
              {selectedBookingIds.length > 0
                ? `Export selected (${selectedBookingIds.length})`
                : "Export shown"}
            </Button>
            </Tooltip>
            {exportProgress && (
              <Progress
                percent={
                  exportProgress.total > 0
                    ? Math.round(
                        (exportProgress.loaded / exportProgress.total) * 100,
                      )
                    : 0
                }
                size="small"
                status="active"
                style={{ width: 160, margin: 0 }}
                format={() =>
                  exportProgress.total > 0
                    ? `${exportProgress.loaded}/${exportProgress.total}`
                    : "..."
                }
              />
            )}
            {selectedBookingIds.length > 0 && (
              <Button
                type="primary"
                icon={<ContainerOutlined />}
                onClick={() => setBulkActionDrawerVisible(true)}
                size="middle"
              >
                Bulk ({selectedBookingIds.length})
              </Button>
            )}
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                loadBookings();
                onRefresh();
              }}
              loading={loading}
              size="middle"
            >
              Refresh
            </Button>
          </div>
      </div>
    );
  };

  // Render bulk action drawer
  const renderBulkActionDrawer = () => {
    const selectedBookings = bookings.filter((booking) =>
      selectedBookingIds.includes(booking.id),
    );

    const selectedNeedAnalyse = selectedBookings.filter(bookingNeedsCiAnalysis);
    const selectedNeedDeepCheck = selectedBookings.filter((booking) => {
      const path = getPrimaryCommercialInvoicePath(
        booking.commercial_invoice_file,
      );
      return Boolean(path && !bookingHasDeepCheckCiAnalysis(booking));
    });

    // Calculate totals for selected bookings
    const selectedBookingsTotals = calculateBookingsTotals(selectedBookings);

    // Calculate totals for existing bookings in container
    const existingBookingsTotals = calculateBookingsTotals(
      existingContainerBookings,
    );

    return (
      <Drawer
        title={`Bulk Actions (${selectedBookingIds.length} bookings selected)`}
        width={500}
        onClose={() => setBulkActionDrawerVisible(false)}
        open={bulkActionDrawerVisible}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button
              onClick={() => setBulkActionDrawerVisible(false)}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkDownloadLabels}
              loading={downloadingLabels}
              disabled={selectedBookingIds.length === 0}
              style={{ marginRight: 8 }}
            >
              📦 Download Labels
            </Button>
            <Button
              onClick={() => setBulkStatusChangeVisible(true)}
              style={{ marginRight: 8 }}
              disabled={selectedBookingIds.length === 0}
            >
              Change Status
            </Button>
            <Button
              type="primary"
              onClick={handleAddToContainer}
              loading={processingBulkAction}
              disabled={!selectedContainerId || selectedBookingIds.length === 0}
            >
              Add to Container
            </Button>
          </div>
        }
      >
        <Alert
          message="Add multiple bookings to a container"
          description="Select a container below to add all selected bookings to it."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Divider orientation="left">Commercial invoice</Divider>
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          <Button
            block
            icon={<FileSearchOutlined />}
            loading={bulkAnalysingCi}
            disabled={
              selectedNeedAnalyse.length === 0 ||
              bulkDeepCheckingCi ||
              bulkAnalysingCi
            }
            onClick={() => handleBulkAnalyseCi(selectedNeedAnalyse)}
          >
            Analyse CI — automatic ({selectedNeedAnalyse.length})
          </Button>
          <Button
            block
            icon={<RobotOutlined />}
            loading={bulkDeepCheckingCi}
            disabled={
              selectedNeedDeepCheck.length === 0 ||
              bulkAnalysingCi ||
              bulkDeepCheckingCi
            }
            onClick={() => handleBulkDeepCheckCi(selectedNeedDeepCheck)}
          >
            AI deep check ({selectedNeedDeepCheck.length})
          </Button>
        </Space>

        <Divider orientation="left">Select Container</Divider>
        <Select
          style={{ width: "100%" }}
          placeholder="Select Container"
          value={selectedContainerId}
          onChange={setSelectedContainerId}
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {containers.map((container) => {
            // Get container origin and destination
            const originPort = container.container_origin_port
              ? Array.isArray(container.container_origin_port)
                ? container.container_origin_port[0]?.name
                : container.container_origin_port.name
              : null;

            const destPort = container.container_destination_port
              ? Array.isArray(container.container_destination_port)
                ? container.container_destination_port[0]?.name
                : container.container_destination_port.name
              : null;

            // Format display text
            const displayText = `${container.reference || container.id} ${
              originPort ? `(${originPort}` : ""
            }${destPort ? ` → ${destPort})` : originPort ? ")" : ""}`;

            return (
              <Option key={container.id} value={container.id.toString()}>
                🚢 {displayText}
              </Option>
            );
          })}
        </Select>

        <Divider orientation="left">Selected Bookings</Divider>

        {/* Summary for selected bookings */}
        <div
          style={{
            display: "flex",
            marginBottom: 8,
            background: "#f5f5f5",
            padding: "8px",
            borderRadius: "4px",
          }}
        >
          <div
            style={{
              flex: 1,
              textAlign: "center",
              borderRight: "1px solid #e8e8e8",
            }}
          >
            <div style={{ fontSize: "11px", color: "#888" }}>Total Weight</div>
            <div style={{ fontWeight: "bold" }}>
              {selectedBookingsTotals.totalWeight.toFixed(2)} kg
            </div>
          </div>
          <div
            style={{
              flex: 1,
              textAlign: "center",
              borderRight: "1px solid #e8e8e8",
            }}
          >
            <div style={{ fontSize: "11px", color: "#888" }}>Total Cartons</div>
            <div style={{ fontWeight: "bold" }}>
              {selectedBookingsTotals.totalCartons}
            </div>
          </div>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: "11px", color: "#888" }}>Total CBM</div>
            <div style={{ fontWeight: "bold" }}>
              {selectedBookingsTotals.totalCBM.toFixed(2)} m³
            </div>
          </div>
        </div>

        <List
          size="small"
          bordered
          dataSource={selectedBookings}
          renderItem={(item) => {
            const reference = getFormattedBookingReference(item);
            // Get company name
            const company = Array.isArray(item.company_info)
              ? item.company_info[0]?.name
              : item.company_info?.name || "Unknown";

            // Get status with color
            const statusColor = getStatusColor(item.status || "");
            const status = item.status
              ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
              : "Unknown";

            // Get transport type
            const transportInfo = item.transport_type_info;
            let transportEmoji = "";
            let transportName = "";

            if (transportInfo) {
              if (Array.isArray(transportInfo) && transportInfo.length > 0) {
                transportName = transportInfo[0].abbreviation || "";
                transportEmoji =
                  transportInfo[0].abbreviation === "AIR"
                    ? "✈️"
                    : transportInfo[0].abbreviation === "SEA"
                      ? "🚢"
                      : "";
              } else if (typeof transportInfo === "object") {
                const typedTransportInfo = transportInfo as TransportType;
                transportName = typedTransportInfo.abbreviation || "";
                transportEmoji =
                  typedTransportInfo.abbreviation === "AIR"
                    ? "✈️"
                    : typedTransportInfo.abbreviation === "SEA"
                      ? "🚢"
                      : "";
              }
            }

            // Get booking type
            const bookingTypeInfo = item.booking_type_info;
            let bookingTypeName = "";

            if (bookingTypeInfo) {
              if (
                Array.isArray(bookingTypeInfo) &&
                bookingTypeInfo.length > 0
              ) {
                bookingTypeName = bookingTypeInfo[0].name || "";
              } else if (typeof bookingTypeInfo === "object") {
                const typedBookingTypeInfo = bookingTypeInfo as BookingType;
                bookingTypeName = typedBookingTypeInfo.name || "";
              }
            }

            // Get product summary for this booking
            const productSummary = getBookingProductSummary(item);

            return (
              <List.Item
                style={{ padding: "4px 8px" }}
                actions={[
                  <Button
                    type="text"
                    size="small"
                    danger
                    onClick={() => {
                      // Remove this booking from selection
                      setSelectedBookingIds((prevIds) =>
                        prevIds.filter((id) => id !== item.id),
                      );
                    }}
                  >
                    Remove
                  </Button>,
                ]}
              >
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: "1 1 auto" }}>
                      <strong>{reference}</strong> - {company}
                    </div>
                    <div
                      style={{ display: "flex", gap: "4px", fontSize: "12px" }}
                    >
                      {item.status && (
                        <Tag color={statusColor} style={{ margin: 0 }}>
                          {status}
                        </Tag>
                      )}
                      {transportName && (
                        <Tag color="blue" style={{ margin: 0 }}>
                          {transportEmoji} {transportName}
                        </Tag>
                      )}
                      {bookingTypeName && (
                        <Tag color="purple" style={{ margin: 0 }}>
                          {bookingTypeName}
                        </Tag>
                      )}
                    </div>
                  </div>

                  {/* Add product summary line */}
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      marginTop: "2px",
                    }}
                  >
                    {productSummary.totalWeight > 0 &&
                      `${productSummary.totalWeight.toFixed(2)} kg · `}
                    {productSummary.totalCartons > 0 &&
                      `${productSummary.totalCartons} cartons · `}
                    {productSummary.totalCBM > 0 &&
                      `${productSummary.totalCBM.toFixed(2)} m³`}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />

        {selectedContainerId && (
          <>
            <Divider orientation="left">
              Existing Bookings in Container{" "}
              {loadingContainerBookings && <small>(Loading...)</small>}
            </Divider>

            {/* Summary for existing bookings */}
            {existingContainerBookings.length > 0 && (
              <div
                style={{
                  display: "flex",
                  marginBottom: 8,
                  background: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    textAlign: "center",
                    borderRight: "1px solid #e8e8e8",
                  }}
                >
                  <div style={{ fontSize: "11px", color: "#888" }}>
                    Total Weight
                  </div>
                  <div style={{ fontWeight: "bold" }}>
                    {existingBookingsTotals.totalWeight.toFixed(2)} kg
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    textAlign: "center",
                    borderRight: "1px solid #e8e8e8",
                  }}
                >
                  <div style={{ fontSize: "11px", color: "#888" }}>
                    Total Cartons
                  </div>
                  <div style={{ fontWeight: "bold" }}>
                    {existingBookingsTotals.totalCartons}
                  </div>
                </div>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: "11px", color: "#888" }}>
                    Total CBM
                  </div>
                  <div style={{ fontWeight: "bold" }}>
                    {existingBookingsTotals.totalCBM.toFixed(2)} m³
                  </div>
                </div>
              </div>
            )}

            <List
              size="small"
              bordered
              loading={loadingContainerBookings}
              dataSource={existingContainerBookings}
              locale={{ emptyText: "No existing bookings in this container" }}
              renderItem={(item) => {
                const reference = getFormattedBookingReference(item);
                const company = Array.isArray(item.company_info)
                  ? item.company_info[0]?.name
                  : item.company_info?.name || "Unknown";

                // Get status with color
                const statusColor = getStatusColor(item.status || "");
                const status = item.status
                  ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                  : "Unknown";

                // Get transport type
                const transportInfo = item.transport_type_info;
                let transportEmoji = "";
                let transportName = "";

                if (transportInfo) {
                  if (
                    Array.isArray(transportInfo) &&
                    transportInfo.length > 0
                  ) {
                    transportName = transportInfo[0].abbreviation || "";
                    transportEmoji =
                      transportInfo[0].abbreviation === "AIR"
                        ? "✈️"
                        : transportInfo[0].abbreviation === "SEA"
                          ? "🚢"
                          : "";
                  } else if (typeof transportInfo === "object") {
                    const typedTransportInfo = transportInfo as TransportType;
                    transportName = typedTransportInfo.abbreviation || "";
                    transportEmoji =
                      typedTransportInfo.abbreviation === "AIR"
                        ? "✈️"
                        : typedTransportInfo.abbreviation === "SEA"
                          ? "🚢"
                          : "";
                  }
                }

                // Get booking type
                const bookingTypeInfo = item.booking_type_info;
                let bookingTypeName = "";

                if (bookingTypeInfo) {
                  if (
                    Array.isArray(bookingTypeInfo) &&
                    bookingTypeInfo.length > 0
                  ) {
                    bookingTypeName = bookingTypeInfo[0].name || "";
                  } else if (typeof bookingTypeInfo === "object") {
                    const typedBookingTypeInfo = bookingTypeInfo as BookingType;
                    bookingTypeName = typedBookingTypeInfo.name || "";
                  }
                }

                // Get product summary for this booking
                const productSummary = getBookingProductSummary(item);

                return (
                  <List.Item style={{ padding: "4px 8px" }}>
                    <div style={{ width: "100%" }}>
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ flex: "1 1 auto", fontSize: "12px" }}>
                          <strong>{reference}</strong> - {company}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            fontSize: "12px",
                          }}
                        >
                          {item.status && (
                            <Tag color={statusColor} style={{ margin: 0 }}>
                              {status}
                            </Tag>
                          )}
                          {transportName && (
                            <Tag color="blue" style={{ margin: 0 }}>
                              {transportEmoji} {transportName}
                            </Tag>
                          )}
                          {bookingTypeName && (
                            <Tag color="purple" style={{ margin: 0 }}>
                              {bookingTypeName}
                            </Tag>
                          )}
                        </div>
                      </div>

                      {/* Add product summary line */}
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#888",
                          marginTop: "2px",
                        }}
                      >
                        {productSummary.totalWeight > 0 &&
                          `${productSummary.totalWeight.toFixed(2)} kg · `}
                        {productSummary.totalCartons > 0 &&
                          `${productSummary.totalCartons} cartons · `}
                        {productSummary.totalCBM > 0 &&
                          `${productSummary.totalCBM.toFixed(2)} m³`}
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />

            {/* Show combined totals if both selected and existing bookings exist */}
            {selectedBookings.length > 0 &&
              existingContainerBookings.length > 0 && (
                <>
                  <Divider orientation="left">Combined Totals</Divider>
                  <div
                    style={{
                      display: "flex",
                      background: "#e6f7ff",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #91d5ff",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        textAlign: "center",
                        borderRight: "1px solid #91d5ff",
                      }}
                    >
                      <div style={{ fontSize: "11px", color: "#0050b3" }}>
                        Total Weight
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {(
                          selectedBookingsTotals.totalWeight +
                          existingBookingsTotals.totalWeight
                        ).toFixed(2)}{" "}
                        kg
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        textAlign: "center",
                        borderRight: "1px solid #91d5ff",
                      }}
                    >
                      <div style={{ fontSize: "11px", color: "#0050b3" }}>
                        Total Cartons
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {selectedBookingsTotals.totalCartons +
                          existingBookingsTotals.totalCartons}
                      </div>
                    </div>
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: "11px", color: "#0050b3" }}>
                        Total CBM
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {(
                          selectedBookingsTotals.totalCBM +
                          existingBookingsTotals.totalCBM
                        ).toFixed(2)}{" "}
                        m³
                      </div>
                    </div>
                  </div>
                </>
              )}
          </>
        )}
      </Drawer>
    );
  };

  // Helper function to calculate totals for a list of bookings
  const calculateBookingsTotals = (bookingsList: Booking[]) => {
    let totalWeight = 0;
    let totalCartons = 0;
    let totalCBM = 0;

    bookingsList.forEach((booking) => {
      const productInfo = booking.product_info;

      if (productInfo) {
        if (Array.isArray(productInfo)) {
          productInfo.forEach((product) => {
            // Use ?? 0 to handle null/undefined values
            totalWeight += product.gross_weight ?? 0;
            totalCartons += product.cartons ?? 0;
            totalCBM += product.cbm ?? 0;
          });
        }
      }
    });

    return { totalWeight, totalCartons, totalCBM };
  };

  // Helper function to get product summary for a single booking
  const getBookingProductSummary = (booking: Booking) => {
    let totalWeight = 0;
    let totalCartons = 0;
    let totalCBM = 0;

    const productInfo = booking.product_info;

    if (productInfo) {
      if (Array.isArray(productInfo)) {
        productInfo.forEach((product) => {
          // Use ?? 0 to handle null/undefined values
          totalWeight += product.gross_weight ?? 0;
          totalCartons += product.cartons ?? 0;
          totalCBM += product.cbm ?? 0;
        });
      }
    }

    return { totalWeight, totalCartons, totalCBM };
  };

  // Handle bulk download of all shipping labels
  const handleBulkDownloadLabels = async () => {
    if (selectedBookingIds.length === 0) {
      message.warning("Please select at least one booking");
      return;
    }

    try {
      setDownloadingLabels(true);
      message.info("Collecting shipping labels...");

      // Fetch all SendCloud shipments for selected bookings
      const { data: shipments, error } = await typedSupabase
        .from("sendcloud_shipments")
        .select("*")
        .in("booking_id", selectedBookingIds);

      if (error) {
        console.error("Error fetching SendCloud shipments:", error);
        message.error("Failed to fetch shipping labels");
        return;
      }

      if (!shipments || shipments.length === 0) {
        message.warning("No shipping labels found for selected bookings");
        return;
      }

      // Filter shipments that have labels (label_url or label_data)
      const shipmentsWithLabels = shipments.filter(
        (shipment) => shipment.label_url,
      );

      if (shipmentsWithLabels.length === 0) {
        message.warning("No shipping labels available for download");
        return;
      }

      message.info(
        `Combining ${shipmentsWithLabels.length} shipping labels...`,
      );

      // Combine all labels into a single PDF
      const combinedPdfBlob = await combineLabelsIntoPDF(
        shipmentsWithLabels,
        (current, total) => {
          console.log(`Processed ${current}/${total} labels`);
        },
      );

      if (!combinedPdfBlob) {
        message.error("Failed to combine shipping labels");
        return;
      }

      // Download the combined PDF
      const filename = `shipping-labels-${selectedBookingIds.length}-bookings-${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      downloadBlob(combinedPdfBlob, filename);

      message.success(
        `Successfully downloaded ${shipmentsWithLabels.length} shipping labels as combined PDF`,
      );
    } catch (error) {
      console.error("Error downloading bulk shipping labels:", error);
      message.error("Failed to download shipping labels");
    } finally {
      setDownloadingLabels(false);
    }
  };

  return (
    <>
      {renderFilters()}

      <CompactTable>
        <Table
          className="compact-table"
          rowSelection={{
            type: "checkbox",
            ...rowSelection,
          }}
          columns={columns}
          dataSource={bookings}
          rowKey="id"
          pagination={
            isPendingList
              ? { pageSize: 10, hideOnSinglePage: true }
              : {
                  ...pagination,
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} bookings`,
                }
          }
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          onRow={(record) => ({
            onClick: (event) => {
              // Don't open booking details if we're processing this booking
              // or if we clicked on a checkbox
              const target = event.target as HTMLElement;
              const isCheckbox =
                target.tagName === "INPUT" &&
                target.getAttribute("type") === "checkbox";

              if (!processingBookingIds.includes(record.id) && !isCheckbox) {
                onViewDetails(record);
              }
            },
            style: { cursor: "pointer" },
          })}
        />
      </CompactTable>

      {renderBulkActionDrawer()}

      {/* Bulk Status Change Modal */}
      <Modal
        title={`Change Status (${selectedBookingIds.length} bookings selected)`}
        open={bulkStatusChangeVisible}
        onOk={handleBulkStatusChange}
        onCancel={() => {
          setBulkStatusChangeVisible(false);
          setSelectedBulkStatus(null);
        }}
        confirmLoading={processingBulkStatus}
        okText="Update Status"
        okButtonProps={{ disabled: !selectedBulkStatus }}
      >
        <Alert
          message="Bulk Status Change"
          description="Select a new status to apply to all selected bookings."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <strong>Select New Status:</strong>
        </div>

        <Select
          style={{ width: "100%" }}
          placeholder="Select a status"
          value={selectedBulkStatus}
          onChange={setSelectedBulkStatus}
          showSearch
          optionFilterProp="children"
        >
          {Object.entries(bookingStatusLabels).map(([value, label]) => (
            <Option key={value} value={value}>
              <Tag color={getStatusColor(value)} style={{ marginRight: 8 }}>
                {label}
              </Tag>
              {label}
            </Option>
          ))}
        </Select>

        <div style={{ marginTop: 16, fontSize: 12, color: "#666" }}>
          This will update the status for all {selectedBookingIds.length}{" "}
          selected booking(s).
        </div>
      </Modal>
    </>
  );
};

export default BookingsList;
